// Browser push notification service.
//
// Wraps Firebase Cloud Messaging (FCM) so the rest of the app can ask
// "is the user opted in?", "request permission and register a token",
// and "drop the token on logout" without dealing with the SDK directly.
//
// Notes:
//   - We register the FCM service worker explicitly with the project's
//     Firebase config in the URL query string. The SW reads those params
//     to bootstrap itself per-environment (dev/staging/prod).
//   - We DO NOT call requestPermission() on app load. The contract for
//     this feature is "opt-in is contextual" — only ask during a
//     processing wait state or from the settings page.

import { isSupported as isMessagingSupported, getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { firebaseConfig, registerPushToken, removePushToken, sendTestPush } from '../firebase';

const SW_PATH = '/firebase-messaging-sw.js';

let messagingPromise = null;
let foregroundUnsub = null;

// Lazily fetch a configured messaging instance, registering the service
// worker with the same config the main app uses. Returns null if FCM is
// not supported in this browser or the config is missing.
async function getMessagingInstance() {
  if (messagingPromise) return messagingPromise;

  messagingPromise = (async () => {
    try {
      const supported = await isMessagingSupported();
      if (!supported) return null;
    } catch (_) {
      return null;
    }

    if (!firebaseConfig || !firebaseConfig.projectId) return null;

    // Reuse the default app initialized in firebase.js. Avoid a second
    // initializeApp() call which would throw "duplicate app".
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(app);
  })();

  return messagingPromise;
}

// Build the SW registration URL with the Firebase config in the query
// string. The SW file itself can't read process.env, so it reads from
// self.location.search.
function buildSwUrl() {
  const params = new URLSearchParams();
  Object.entries(firebaseConfig || {}).forEach(([k, v]) => {
    if (v) params.set(k, String(v));
  });
  return `${SW_PATH}?${params.toString()}`;
}

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  // If a registration already exists at our scope, reuse it. Otherwise
  // register fresh. Browsers will diff the SW file and only update if it
  // has actually changed.
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing && existing.active && existing.active.scriptURL.includes(SW_PATH)) {
    return existing;
  }
  const registration = await navigator.serviceWorker.register(buildSwUrl(), { scope: '/' });
  // Wait for the service worker to activate — getToken() requires an active SW.
  // Reject on `redundant` (failed activation) or missing worker so callers see
  // a registration failure instead of a hung promise.
  if (!registration.active) {
    await new Promise((resolve, reject) => {
      const sw = registration.installing || registration.waiting;
      if (!sw) {
        reject(new Error('No service worker available for activation'));
        return;
      }
      sw.addEventListener('statechange', function onStateChange() {
        if (sw.state === 'activated') {
          sw.removeEventListener('statechange', onStateChange);
          resolve();
        } else if (sw.state === 'redundant') {
          sw.removeEventListener('statechange', onStateChange);
          reject(new Error('Service worker became redundant before activating'));
        }
      });
    });
  }
  return registration;
}

// Public: returns the browser-level permission state without prompting.
// One of: 'granted' | 'denied' | 'default' | 'unsupported'.
export function getPermissionState() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

// Public: best-effort detection that push is supported in this browser.
// Returns false on browsers without ServiceWorker, PushManager, or where
// FCM's own isSupported() check fails.
export async function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
    return false;
  }
  try {
    return await isMessagingSupported();
  } catch (_) {
    return false;
  }
}

// Public: prompt the user for permission, register the SW, get an FCM
// token, and persist it to Firestore (server-side, via callable function
// `register_push_token`). Returns:
//   { ok: true, token, permission }
//   { ok: false, reason: 'unsupported' | 'denied' | 'no-vapid' | 'token-failed', error? }
export async function enablePushNotifications() {
  if (!(await isPushSupported())) {
    return { ok: false, reason: 'unsupported' };
  }

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    // The VAPID public key is required by FCM Web Push. Without it
    // getToken() throws — surface a clean reason for the caller.
    return { ok: false, reason: 'no-vapid' };
  }

  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    return { ok: false, reason: 'denied', error: e };
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied', permission };
  }

  let registration;
  try {
    registration = await registerServiceWorker();
  } catch (e) {
    console.error('[Push] Service worker registration failed:', e);
    return { ok: false, reason: 'token-failed', error: e };
  }

  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: 'unsupported' };

  let token;
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration || undefined,
    });
  } catch (e) {
    console.error('[Push] FCM getToken failed:', e);
    return { ok: false, reason: 'token-failed', error: e };
  }
  if (!token) return { ok: false, reason: 'token-failed' };

  try {
    await registerPushToken({
      token,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
  } catch (e) {
    // The browser side has the token but Firestore write failed. Surface
    // the failure so the caller can show an error and retry.
    console.error('[Push] registerPushToken callable failed:', e);
    return { ok: false, reason: 'token-failed', error: e, token };
  }

  return { ok: true, token, permission };
}

// Public: subscribe to foreground messages. Returns an unsubscribe fn.
// Components use this to render an in-app toast/banner instead of a
// system notification when the user is already on the page.
export async function onForegroundMessage(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  if (foregroundUnsub) foregroundUnsub();
  foregroundUnsub = onMessage(messaging, handler);
  return () => {
    if (foregroundUnsub) {
      foregroundUnsub();
      foregroundUnsub = null;
    }
  };
}

// Public: short, non-PII fingerprint of an FCM token for display.
// Returns the first 12 chars of the token's SHA-256 hex digest.
// Matches the server-side fingerprint produced in `send_test_push` so the
// UI can highlight which entry on the user's account belongs to this
// browser.
export async function fingerprintToken(token) {
  if (!token || typeof crypto === 'undefined' || !crypto.subtle) return '';
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 12);
  } catch (_) {
    return '';
  }
}

// Public: read-only inspection of the FCM/service-worker state for this
// browser. Used by the account-settings diagnostics panel. Never prompts
// the user — returns `{ permission: 'default' }` if permission hasn't been
// granted yet so we don't accidentally trigger a permission dialog.
export async function getPushDiagnostics() {
  const diag = {
    supported: false,
    permission: getPermissionState(),
    serviceWorker: 'unknown',
    serviceWorkerScope: null,
    token: null,
    tokenFingerprint: null,
    vapidConfigured: Boolean(process.env.REACT_APP_FIREBASE_VAPID_KEY),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };

  diag.supported = await isPushSupported();
  if (!diag.supported) return diag;

  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (!reg) {
        diag.serviceWorker = 'not-registered';
      } else if (reg.active && reg.active.scriptURL.includes(SW_PATH)) {
        diag.serviceWorker = 'active';
        diag.serviceWorkerScope = reg.scope;
      } else if (reg.installing) {
        diag.serviceWorker = 'installing';
      } else if (reg.waiting) {
        diag.serviceWorker = 'waiting';
      } else {
        diag.serviceWorker = 'inactive';
      }
    } catch (_) {
      diag.serviceWorker = 'error';
    }
  }

  if (diag.permission === 'granted' && diag.vapidConfigured) {
    try {
      const messaging = await getMessagingInstance();
      if (messaging) {
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });
        if (token) {
          diag.token = token;
          diag.tokenFingerprint = await fingerprintToken(token);
        }
      }
    } catch (e) {
      // Token retrieval can fail if the SW just installed and hasn't
      // been claimed yet. Don't escalate — the panel can show "unknown".
      diag.tokenError = e?.message || String(e);
    }
  }

  return diag;
}

// Public: fire a real "test notification" through the callable backend
// to every FCM token registered for the signed-in user. Returns the raw
// callable response so the UI can render per-device results.
//
// We forward window.location.origin so the backend can build a valid
// absolute https URL for WebpushFCMOptions.link — firebase-admin rejects
// relative paths at JSON-encode time and the whole multicast errors out
// before it ships.
export async function triggerTestPush() {
  const origin = typeof window !== 'undefined' && window.location
    ? window.location.origin
    : '';
  const res = await sendTestPush({ origin });
  return res?.data || {};
}

// Public: revoke the token both in FCM and in our Firestore record.
// Called on sign-out and when the user disables notifications.
export async function disablePushNotifications() {
  // If notification permission was never granted, there's no token to revoke.
  if (getPermissionState() !== 'granted') {
    return { ok: true };
  }
  const messaging = await getMessagingInstance();
  let token = null;
  if (messaging) {
    try {
      // Try to grab the current token first so we can drop the right doc.
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (vapidKey) {
        token = await getToken(messaging, { vapidKey }).catch(() => null);
      }
    } catch (_) { /* noop */ }
    try {
      await deleteToken(messaging);
    } catch (_) { /* noop */ }
  }
  if (token) {
    try {
      await removePushToken({ token });
    } catch (_) { /* noop */ }
  }
  return { ok: true };
}
